import { Mic } from "lucide-react";

import { ConfirmationModal } from "@components/ConfirmationModal";

type RecordingNavWarningModalProps = {
  isOpen: boolean;
  /** Examinee whose recording will end — makes the single-case scope explicit. */
  examineeName: string | null;
  /** Keep the session running and cancel the navigation (safe default). */
  onStay: () => void;
  /** Stop + save the session, then proceed with the navigation. */
  onFinishAndLeave: () => void;
};

/**
 * Shown when a live recording is about to leave its owning case (case switch,
 * case list, logout, or any non-case page) — UAT-03 / UAT-04.
 *
 * Two actions only, prioritising the safe default: "Stay on this case" keeps
 * recording (mapped to the modal's cancel/left slot); "Finish recording and
 * leave" is the destructive action that stops and saves before navigating.
 * Recording never crosses a case boundary, so there is deliberately no
 * "keep recording" option.
 */
export const RecordingNavWarningModal = ({
  isOpen,
  examineeName,
  onStay,
  onFinishAndLeave,
}: RecordingNavWarningModalProps) => {
  const who = examineeName ? ` for ${examineeName}` : "";

  return (
    <ConfirmationModal
      open={isOpen}
      onOpenChange={(open) => {
        // Dismissing the dialog (Esc / overlay) is the safe choice: stay.
        if (!open) onStay();
      }}
      type="warning"
      icon={<Mic className="size-10 text-destructive" strokeWidth={2} />}
      title="Recording still in progress"
      description={`Leaving this case will end the active recording${who}. Finish and save it first, or stay on this case to keep recording.`}
      confirmButtonText="Finish recording and leave"
      cancelButtonText="Stay on this case"
      onConfirm={onFinishAndLeave}
      onCancel={onStay}
    />
  );
};
